/**
 * Rax Data Deletion Component (GDPR Compliant)
 *
 * Allows users to request complete deletion of their account and all data.
 * Implements GDPR-compliant deletion with confirmation.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { icons } from "../icons.js";
import { raxTheme } from "../styles/rax-theme.js";

@customElement("rax-data-deletion")
export class RaxDataDeletion extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .deletion-container {
        padding: var(--space-6);
        max-width: 600px;
      }
      
      .deletion-header {
        margin-bottom: var(--space-6);
      }
      
      .deletion-title {
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--error);
        margin-bottom: var(--space-2);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .deletion-title svg {
        width: 24px;
        height: 24px;
        stroke: currentColor;
        fill: none;
        stroke-width: 1.8px;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      
      .deletion-description {
        color: var(--text-secondary);
        font-size: var(--font-sm);
        line-height: 1.5;
      }
      
      /* Warning Box */
      .warning-box {
        margin: var(--space-6) 0;
        padding: var(--space-4);
        background: rgba(239, 68, 68, 0.08);
        border: 1px solid rgba(239, 68, 68, 0.4);
        border-radius: var(--radius-md);
      }
      
      .warning-box-title {
        font-weight: 600;
        color: var(--error);
        margin-bottom: var(--space-2);
        font-size: var(--font-sm);
      }
      
      .warning-box-content {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        line-height: 1.5;
      }
      
      /* Data List */
      .data-list {
        margin-top: var(--space-6);
        padding: var(--space-4);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }
      
      .data-list-title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-3);
        font-size: var(--font-sm);
      }
      
      .data-list ul {
        margin: 0;
        padding-left: var(--space-5);
        color: var(--text-secondary);
        font-size: var(--font-sm);
        line-height: 1.7;
      }
      
      .data-list li {
        margin-bottom: var(--space-1);
      }
      
      /* Confirmation Section */
      .confirmation-section {
        margin-top: var(--space-6);
        padding: var(--space-5);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .confirmation-section.active {
        border-color: var(--error);
      }
      
      .confirmation-label {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
        font-size: var(--font-sm);
      }
      
      .confirmation-hint {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        margin-bottom: var(--space-3);
      }
      
      .confirmation-input {
        width: 100%;
        padding: var(--space-3);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        font-family: inherit;
        transition: border-color var(--transition-fast);
        box-sizing: border-box;
      }
      
      .confirmation-input:focus {
        outline: none;
        border-color: var(--error);
      }
      
      .confirmation-input::placeholder {
        color: var(--text-tertiary);
      }
      
      .confirmation-input.error {
        border-color: var(--error);
      }
      
      /* Delete Button */
      .delete-actions {
        margin-top: var(--space-4);
        display: flex;
        gap: var(--space-3);
        align-items: center;
      }
      
      .delete-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-6);
        background: var(--error);
        color: white;
        border: none;
        border-radius: var(--radius-md);
        font-weight: 600;
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .delete-btn:hover:not(:disabled) {
        background: #dc2626;
        box-shadow: 0 0 16px rgba(239, 68, 68, 0.4);
      }
      
      .delete-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .delete-btn svg {
        width: 16px;
        height: 16px;
        stroke: currentColor;
        fill: none;
      }
      
      .cancel-btn {
        padding: var(--space-3) var(--space-4);
        background: transparent;
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .cancel-btn:hover {
        color: var(--text-primary);
        border-color: var(--text-tertiary);
      }
      
      /* Status Messages */
      .status-message {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-sm);
      }
      
      .status-message.error {
        color: var(--error);
      }
      
      .status-message.loading {
        color: var(--text-secondary);
      }
      
      .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid var(--border-color);
        border-top-color: var(--error);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      
      /* Start Button */
      .start-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-5);
        background: var(--bg-secondary);
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-weight: 500;
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .start-btn:hover {
        background: rgba(239, 68, 68, 0.08);
        border-color: var(--error);
        color: var(--error);
      }
      
      .start-btn svg {
        width: 16px;
        height: 16px;
        stroke: currentColor;
        fill: none;
      }
    `,
  ];

  @state()
  private showConfirmation = false;

  @state()
  private confirmationText = "";

  @state()
  private isDeleting = false;

  @state()
  private error: string | null = null;

  @state()
  private userEmail = "";

  private confirmationPhrase = "DELETE MY DATA";

  override async connectedCallback() {
    super.connectedCallback();
    // Fetch user email for display
    try {
      const result = await raxApi.getCurrentUser();
      if (result.success && result.data) {
        this.userEmail = result.data.email || "";
      }
    } catch {
      // Ignore errors fetching user
    }
  }

  private get isConfirmationValid(): boolean {
    return this.confirmationText.trim().toUpperCase() === this.confirmationPhrase;
  }

  private handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.confirmationText = input.value;
    this.error = null;
  }

  private handleStartDeletion() {
    this.showConfirmation = true;
    this.confirmationText = "";
    this.error = null;
  }

  private handleCancelDeletion() {
    this.showConfirmation = false;
    this.confirmationText = "";
    this.error = null;
  }

  private async handleDeleteAccount() {
    if (!this.isConfirmationValid) {
      this.error = `Please type "${this.confirmationPhrase}" exactly to confirm.`;
      return;
    }

    this.isDeleting = true;
    this.error = null;

    try {
      const result = await raxApi.deleteAccount(this.confirmationPhrase);

      if (result.success) {
        // Dispatch event for logout
        this.dispatchEvent(
          new CustomEvent("account-deleted", {
            bubbles: true,
            composed: true,
          }),
        );

        // Sign out via Clerk
        const clerk = (window as any).Clerk || (window as any).clerkClient;
        if (clerk?.signOut) {
          await clerk.signOut();
        }

        // Redirect to homepage
        window.location.href = "/";
      } else {
        this.error = result.error || "Failed to delete account. Please try again.";
      }
    } catch (err) {
      console.error("[DataDeletion] Delete error:", err);
      this.error = err instanceof Error ? err.message : "An error occurred. Please try again.";
    } finally {
      this.isDeleting = false;
    }
  }

  override render() {
    return html`
      <div class="deletion-container">
        <div class="deletion-header">
          <h2 class="deletion-title">
            ${
              icons.trash ||
              html`
                <svg viewBox="0 0 24 24">
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
              `
            }
            Delete My Data
          </h2>
          <p class="deletion-description">
            Request permanent deletion of your account and all associated data.
            This action cannot be undone.
          </p>
        </div>

        <div class="warning-box">
          <div class="warning-box-title">Warning: This action is irreversible</div>
          <div class="warning-box-content">
            Once you delete your account, all your data will be permanently removed from our systems.
            You will not be able to recover any information, and any active subscriptions will be cancelled.
          </div>
        </div>

        <div class="data-list">
          <div class="data-list-title">The following data will be permanently deleted:</div>
          <ul>
            <li><strong>Conversation history</strong> - All messages and chat sessions with your agents</li>
            <li><strong>Activity logs</strong> - Your complete audit trail and usage history</li>
            <li><strong>Learning ledger</strong> - All learned preferences and personalization data</li>
            <li><strong>Stored secrets</strong> - API keys, credentials, and integration tokens</li>
            <li><strong>Agent configurations</strong> - All agent settings, personas, and custom rules</li>
            <li><strong>Automation rules</strong> - Any scheduled tasks or event-based automations</li>
            <li><strong>Account information</strong> - Your profile, preferences, and billing history</li>
          </ul>
        </div>

        ${
          this.showConfirmation
            ? html`
          <div class="confirmation-section active">
            <div class="confirmation-label">Confirm Deletion</div>
            <div class="confirmation-hint">
              Type <strong>${this.confirmationPhrase}</strong> below to confirm you want to permanently delete your account.
            </div>
            <input
              type="text"
              class="confirmation-input ${this.error ? "error" : ""}"
              placeholder="Type ${this.confirmationPhrase} to confirm"
              .value=${this.confirmationText}
              @input=${this.handleInputChange}
              ?disabled=${this.isDeleting}
              autocomplete="off"
              spellcheck="false"
            />

            <div class="delete-actions">
              <button
                class="delete-btn"
                @click=${this.handleDeleteAccount}
                ?disabled=${this.isDeleting || !this.isConfirmationValid}
              >
                ${
                  this.isDeleting
                    ? html`
                        <div class="spinner"></div>
                        Deleting...
                      `
                    : html`
                        <svg viewBox="0 0 24 24">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                        Delete Everything
                      `
                }
              </button>
              <button
                class="cancel-btn"
                @click=${this.handleCancelDeletion}
                ?disabled=${this.isDeleting}
              >
                Cancel
              </button>

              ${
                this.error
                  ? html`
                <span class="status-message error">
                  ${this.error}
                </span>
              `
                  : ""
              }
            </div>
          </div>
        `
            : html`
          <div class="confirmation-section">
            <div class="confirmation-label">Ready to delete your account?</div>
            <div class="confirmation-hint">
              We recommend exporting your data first using the Export Data option above.
            </div>
            <button class="start-btn" @click=${this.handleStartDeletion}>
              <svg viewBox="0 0 24 24"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
              Begin Account Deletion
            </button>
          </div>
        `
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-data-deletion": RaxDataDeletion;
  }
}
